import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import ListItem from "../../ListItem";
import SectionContainer from "../../SectionContainer";
import styles from "../PropertyPolicies.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import { Suspense } from "react";
import getDisplayIcon from "../../PropertyPolicies/components/utils";
import HouseRulesDefaultIcon from "@Icons/HouseRulesDefualtIcon";
import CancellationPolicyIcon from "@Icons/CancellationPolicyIcon";
import PolicyIconContainer from "../../shared/PolicyIconContainer";
import { POLICY_ICON_PROPS } from "../../shared/policyIconConstants";

const HouseRulePolicies = ({
  title,
  policies,
  gtagEventAction,
  isDesktop = false,
  dataTestId = "",
}: any) => {
  const { t } = useTranslationHook();

  const handleCheckIcon = (policy_applicable: any, policy_applicable_en: any) => {
    const val = policy_applicable_en ?? policy_applicable;
    if (typeof val === "string") {
      return val?.toLowerCase() === "true" || val === "真的";
    }
    if (typeof val === "boolean") {
      return val;
    }
    if (val == undefined) {
      return true;
    }
    return false;
  };

  const applicablePolicies = [...(policies || [])].filter(
    (it) => handleCheckIcon(it.policy_applicable, it.policy_applicable_en) === true,
  );

  const visiblePolicies = applicablePolicies;

  // Separate cancellation policies and house rules from visible policies
  const cancellationPolicies = visiblePolicies.filter(
    (p) => p.name === "cancellation_policy" || p.name_en === "cancellation_policy",
  );
  const houseRulesPolicies = visiblePolicies.filter(
    (p) => p.name !== "cancellation_policy" && p.name_en !== "cancellation_policy",
  );

  // Check if we need to show sections
  const showCancellationSection = cancellationPolicies.length > 0;
  const showHouseRulesSection = houseRulesPolicies.length > 0;

  const renderPolicyIcon = (policy: any) =>
    typeof window !== "undefined" ? (
      <Suspense
        fallback={
          <PolicyIconContainer>
            <HouseRulesDefaultIcon {...POLICY_ICON_PROPS} />
          </PolicyIconContainer>
        }
      >
        {getDisplayIcon(policy?.tag_en ?? policy?.tag ?? policy?.name)}
      </Suspense>
    ) : (
      <PolicyIconContainer>
        <HouseRulesDefaultIcon {...POLICY_ICON_PROPS} />
      </PolicyIconContainer>
    );

  return (
    <>
      {showCancellationSection || showHouseRulesSection ? (
        <div className={formatClassNames(styles.policiesContainer, isDesktop && styles.desktop)}>
          {!isDesktop && (
            <div className={styles.header}>
              <div className={styles.title}>{t(title)}</div>
            </div>
          )}
          <SectionContainer
            title={isDesktop ? `${t(title)}` : null}
            className={formatClassNames(styles.policiesSection, isDesktop && styles.desktop)}
            id="house_rules"
            dataTestId={`house_rules-${dataTestId}`}
            showTitleLine={isDesktop}
            headingLevel={2}
          >
            {/* Cancellation Policies Section with Two-Column Layout */}
            {showCancellationSection && (
              <div
                className={formatClassNames(
                  styles.cancellationSection,
                  isDesktop && styles.desktop,
                )}
              >
                <div className={styles.cancellationLeftColumn}>
                  <div className={styles.cancellationIconTitle}>
                    <PolicyIconContainer className={styles.cancellationIcon}>
                      {typeof window !== "undefined" ? (
                        <Suspense fallback={<HouseRulesDefaultIcon {...POLICY_ICON_PROPS} />}>
                          <CancellationPolicyIcon {...POLICY_ICON_PROPS} />
                        </Suspense>
                      ) : (
                        <HouseRulesDefaultIcon {...POLICY_ICON_PROPS} />
                      )}
                    </PolicyIconContainer>
                    <div className={styles.cancellationTitle}>
                      {t("propertypage.propertypolicies.cancellationpoliciestitle")}
                    </div>
                  </div>
                </div>

                <div className={styles.cancellationRightColumn}>
                  {cancellationPolicies.map((policy: any, index: number) => (
                    <ListItem
                      key={`${policy?.value}-${index}`}
                      title={policy?.display_name}
                      index={index}
                      content={t(policy?.value)}
                      gtagEventAction={gtagEventAction}
                      shortText={t(policy?.value)}
                      withIcon={false}
                      isDesktop={isDesktop}
                      isHouseRules
                      isCancellation
                      disableOnClick={false}
                      minimizeContentCharLength={500}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Regular House Rules with Two-Column Layout */}
            {showHouseRulesSection && (
              <div
                className={formatClassNames(styles.houseRulesSection, isDesktop && styles.desktop)}
              >
                <div className={styles.houseRulesRulesList}>
                  {houseRulesPolicies.map((policy: any, index: number) => (
                    <div
                      key={`${policy?.value}-${index}`}
                      className={formatClassNames(
                        styles.houseRulesRow,
                        isDesktop && styles.desktop,
                      )}
                    >
                      {isDesktop ? (
                        <>
                          <div className={styles.houseRulesLeftColumn}>
                            <div className={styles.houseRulesIconTitle}>
                              <div className={styles.houseRulesIcon}>
                                {renderPolicyIcon(policy)}
                              </div>
                              <div className={styles.houseRulesTitle}>{policy?.display_name}</div>
                            </div>
                          </div>
                          <div className={styles.houseRulesRightColumn}>
                            <ListItem
                              title={policy?.display_name}
                              titleClassName={styles.hiddenTitle}
                              index={index}
                              content={t(policy?.value)}
                              gtagEventAction={gtagEventAction}
                              shortText={t(policy?.value)}
                              withIcon={false}
                              isDesktop={isDesktop}
                              isHouseRules
                              disableOnClick={false}
                              minimizeContentCharLength={500}
                            />
                          </div>
                        </>
                      ) : (
                        <div className={styles.houseRulesMobileRow}>
                          <div className={styles.houseRulesIconTitle}>
                            <div className={styles.houseRulesIcon}>{renderPolicyIcon(policy)}</div>
                            <div className={styles.houseRulesTitle}>{policy?.display_name}</div>
                          </div>
                          <div className={styles.houseRulesMobileContent}>
                            <ListItem
                              title={policy?.display_name}
                              titleClassName={styles.hiddenTitle}
                              index={index}
                              content={t(policy?.value)}
                              gtagEventAction={gtagEventAction}
                              shortText={t(policy?.value)}
                              withIcon={false}
                              isDesktop={isDesktop}
                              isHouseRules
                              disableOnClick={false}
                              minimizeContentCharLength={500}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SectionContainer>
        </div>
      ) : null}
    </>
  );
};

export default wrapperHOC(HouseRulePolicies, {
  componentName: "HouseRulePolicies-PropertyPage",
  showForChina: true,
});
